import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../config";
import { drawFakeBanScreen } from "./BanScreen";
import { isValidName, needsRename, sanitizeName } from "../utils/name";
import { promptModal } from "../utils/prompt";
import { Clock } from "./Clock";
import { Renderer } from "./Renderer";
import { BanScene } from "./scenes/BanScene";
import { MenuScene } from "./scenes/MenuScene";
import { SceneManager } from "./scenes/SceneManager";
import type { GameContext, Scene } from "./scenes/Scene";
import { Audio } from "./systems/Audio";
import { ButtonLayer } from "./systems/Buttons";
import { Effects } from "./systems/Effects";
import { Input } from "./systems/Input";
import { Battles } from "./systems/Battles";
import { Friends } from "./systems/Friends";
import { Leaderboard } from "./systems/Leaderboard";
import { Chat } from "./systems/Chat";
import { Clans } from "./systems/Clans";
import { Daily } from "./systems/Daily";
import { Slots } from "./systems/Slots";
import { Jumpscare } from "./systems/Jumpscare";
import { Mods } from "./systems/Mods";
import { Quests } from "./systems/Quests";
import { Races } from "./systems/Races";
import { Score } from "./systems/Score";
import { Settings } from "./systems/Settings";
import { Shop } from "./systems/Shop";

export class Game {
  private readonly renderer: Renderer;
  private readonly input: Input;
  private readonly scenes = new SceneManager();
  private readonly clock = new Clock();
  private readonly score = new Score();
  private readonly settings = new Settings();
  private readonly audio: Audio;
  private readonly effects = new Effects();
  private readonly buttons = new ButtonLayer();
  private readonly leaderboard = new Leaderboard();
  private readonly shop = new Shop(this.settings);
  private readonly battles = new Battles();
  private readonly races = new Races();
  private readonly friends = new Friends();
  private readonly quests = new Quests();
  private readonly chat = new Chat();
  private readonly clans = new Clans();
  private readonly daily = new Daily();
  private readonly slots = new Slots();
  private readonly jumpscare = new Jumpscare(this.settings);
  /** Last seen jumpscare flag, to fire a pushed jumpscare once on its rising edge. */
  private prevJumpscareFlag = false;
  private readonly mods = new Mods();
  private rafId = 0;
  private lastTime = 0;
  private running = false;
  private modPollTimer = 0;
  /** True while a forced-rename prompt is open, so the poll can't stack another. */
  private renaming = false;
  /** True while the player is locked into the BanScene (an active server ban). */
  private banLockActive = false;
  /** The shared GameContext, kept so the poll can swap scenes (ban lockout). */
  private readonly ctx: GameContext;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.input = new Input(this.renderer);
    this.audio = new Audio(this.settings);

    const goTo = (scene: Scene): void => this.scenes.goTo(scene);
    const ctxMgr: GameContext = {
      goTo,
      audio: this.audio,
      effects: this.effects,
      score: this.score,
      settings: this.settings,
      buttons: this.buttons,
      leaderboard: this.leaderboard,
      shop: this.shop,
      battles: this.battles,
      races: this.races,
      friends: this.friends,
      quests: this.quests,
      chat: this.chat,
      clans: this.clans,
      daily: this.daily,
      slots: this.slots,
      jumpscare: this.jumpscare,
      renderer: this.renderer,
      mods: this.mods,
      promptForName: () => this.promptForName(),
      promptForLogin: () => this.promptForLogin(),
    };
    this.ctx = ctxMgr;

    this.input.onPress((x, y) => this.buttons.press(x, y));
    this.input.onFlap(() => {
      this.audio.resume();
      this.scenes.flap();
    });
    this.input.onPauseToggle(() => this.scenes.pauseToggle());
    this.input.onToggleMute(() => {
      const muted = this.settings.toggleMuted();
      this.audio.setMuted(muted);
    });

    this.scenes.goTo(new MenuScene(ctxMgr));
    void this.leaderboard.refresh();
    // Admins manage their own mods locally via the mod menu; flag them so the
    // self-poll won't clobber a locally-set noclip/flag. The owner is known by
    // name immediately; granted admins are known once the shop row loads.
    this.updateSelfManaged();
    if (this.settings.isLoggedIn()) {
      void this.shop.refresh().then(() => this.updateSelfManaged());
    }
    // Pending mods are a public read keyed only by name, so poll as soon as a
    // name exists — don't require an authToken (players who claimed a name
    // before accounts existed have no token and would never receive pushes).
    if (this.settings.hasName()) {
      void this.pollMods();
    }
    // Poll for admin-pushed mods/force-rename. Kept slow (30s) and skipped for
    // hidden tabs (in pollMods) to stay well under the hosting request budget —
    // left-open tabs polling fast are what exhausted the free Workers limit.
    this.modPollTimer = window.setInterval(() => void this.pollMods(), 30000);
    // Presence heartbeat + friends/requests/invites refresh while logged in. The
    // GET also bumps last_seen server-side so friends see us as online.
    if (this.settings.isLoggedIn()) {
      void this.friends.refresh(this.settings.playerName, this.settings.authToken);
      void this.quests.refresh(this.settings.playerName, this.settings.authToken);
      void this.daily.refresh(this.settings.playerName, this.settings.authToken);
    }
    window.setInterval(() => {
      if (document.hidden) return;
      if (this.settings.isLoggedIn()) {
        void this.friends.refresh(this.settings.playerName, this.settings.authToken);
        void this.quests.refresh(this.settings.playerName, this.settings.authToken);
      }
    }, 30000);
    document.addEventListener("visibilitychange", this.onVisibility);

    // Spaces are no longer allowed in names. Anyone still holding a spaced name
    // (except the grandfathered owner) is forced to re-pick one; their progress
    // is migrated server-side so nothing is lost.
    if (needsRename(this.settings.playerName)) void this.forceRename("spaces");
  }

  /**
   * Poll our pending mods, then honour an admin-pushed force-rename. Kept
   * together so the rename prompt fires the moment the flag arrives.
   */
  private async pollMods(): Promise<void> {
    // Don't poll backgrounded/minimized tabs — they can't act on a prank anyway,
    // and left-open tabs are the single biggest source of idle API traffic.
    if (document.hidden) return;
    if (!this.settings.hasName()) return;
    await this.mods.pollSelf(this.settings.playerName);
    // A ban takes precedence over every other pushed flag: lock the client into
    // the BanScene and skip pranks/rename. If the ban is lifted, drop back to the
    // menu. The server independently refuses their login and run submissions.
    this.enforceBan();
    if (this.mods.banned) return;
    // A pushed jumpscare fires the moment it arrives — wherever the victim is, not
    // just mid-game — on the flag's rising edge so it triggers once per push (and
    // again on reload while still flagged). Immune accounts are filtered in fire().
    const js = this.mods.flags.jumpscare;
    if (js && !this.prevJumpscareFlag) this.jumpscare.fire();
    this.prevJumpscareFlag = js;
    if (this.mods.forceRenameRequested) void this.forceRename("admin");
  }

  /** Enter/leave the full-screen ban lockout on the rising/falling edge of a ban. */
  private enforceBan(): void {
    if (this.mods.banned && !this.banLockActive) {
      this.banLockActive = true;
      this.scenes.goTo(new BanScene(this.ctx));
    } else if (!this.mods.banned && this.banLockActive) {
      this.banLockActive = false;
      this.scenes.goTo(new MenuScene(this.ctx));
    }
  }

  /**
   * Block until the player re-picks a name. Triggered either locally (a legacy
   * spaced name, no longer allowed) or by an admin push (`force_rename`, arriving
   * via the self-poll). Their account row (score, coins, unlocks, cosmetics,
   * admin) is renamed server-side, so all progress carries over. Bails on the
   * first hard failure and simply re-prompts on the next load/poll. The `renaming`
   * guard keeps the 2.5s poll from stacking a second prompt over an open one.
   */
  private async forceRename(reason: "spaces" | "admin"): Promise<void> {
    if (this.renaming) return;
    const oldName = this.settings.playerName;
    if (!oldName) return;
    if (reason === "spaces" && !needsRename(oldName)) return;
    // The grandfathered owner is never force-renamed (the server blocks it too).
    if (this.mods.isAdmin(oldName)) return;
    this.renaming = true;
    try {
      window.alert(
        reason === "spaces"
          ? `Names can no longer contain spaces. Please pick a new name for "${oldName}".\n` +
              `Your score, coins, and unlocks will carry over.`
          : `An admin has required you to change your name. Please pick a new name for "${oldName}".\n` +
              `Your score, coins, and unlocks will carry over.`,
      );
      for (;;) {
        const next = sanitizeName(
          window.prompt(`New name for "${oldName}" (letters, digits, - or _, no spaces, max 14):`, "") ?? "",
        );
        if (!next) return; // cancelled — they'll be prompted again next load/poll
        if (!isValidName(next)) {
          window.alert("Use only letters, digits, '-' or '_' — no spaces.");
          continue;
        }
        if (next.toLowerCase() === oldName.toLowerCase()) {
          window.alert("Pick a name different from your current one.");
          continue;
        }

        const hadToken = this.settings.authToken.length > 0;
        let result = await this.leaderboard.renameAccount(oldName, next, this.settings.authToken);
        if (result.kind === "forbidden") {
          // The account has a password but isn't logged in here — confirm with it.
          const pw = window.prompt(`Enter the password for "${oldName}" to confirm:`, "") ?? "";
          if (pw) result = await this.leaderboard.renameAccount(oldName, next, this.settings.authToken, pw);
        }

        switch (result.kind) {
          case "ok":
            this.mods.forceRenameRequested = false;
            if (hadToken && result.token) this.settings.setAuth(result.name, result.token);
            else this.settings.setPlayerName(result.name);
            await this.shop.refresh();
            this.updateSelfManaged();
            void this.mods.pollSelf(result.name);
            window.alert(`Done — you're now "${result.name}". Your progress was kept.`);
            return;
          case "not_found":
            // No server-side row for the old name; just adopt the new one locally.
            this.mods.forceRenameRequested = false;
            this.settings.setPlayerName(next);
            return;
          case "taken":
            window.alert(`"${next}" is already taken. Pick another.`);
            continue;
          case "blocked":
            window.alert("That name isn't allowed. Pick another.");
            continue;
          case "invalid":
            window.alert("Use only letters, digits, '-' or '_' — no spaces.");
            continue;
          case "forbidden":
            window.alert("Couldn't verify that this account is yours. Try again.");
            return;
          default:
            window.alert("Couldn't reach the server. Try again later.");
            return;
        }
      }
    } finally {
      this.renaming = false;
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    if (this.modPollTimer) window.clearInterval(this.modPollTimer);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.input.dispose();
    this.renderer.dispose();
  }

  /** An admin/owner self-manages mods locally, so the self-poll is skipped. */
  private updateSelfManaged(): void {
    this.mods.selfManaged = this.mods.isAdmin(this.settings.playerName) || this.shop.isAdmin();
  }

  private async promptForName(): Promise<boolean> {
    if (this.settings.isLoggedIn()) return true;

    const raw = window.prompt("Choose a name (English letters/digits, max 14 chars):", "") ?? "";
    const trimmed = sanitizeName(raw);
    if (trimmed.length === 0) return false;
    if (!isValidName(trimmed)) {
      window.alert("Name must use only English letters, digits, '-' or '_' — no spaces.");
      return false;
    }

    const password =
      (await promptModal({
        message: `Choose a password for "${trimmed}" (4–64 chars):`,
        password: true,
        maxLength: 64,
      })) ?? "";
    if (password.length < 4) {
      window.alert("Password must be at least 4 characters.");
      return false;
    }

    const result = await this.leaderboard.claimName(trimmed, password);
    if (result.kind === "taken") {
      window.alert(`The name "${trimmed}" is already taken. Use "Log in" if it's yours.`);
      return false;
    }
    if (result.kind === "invalid") {
      window.alert("Name must use only English letters, digits, '-' or '_' — no spaces.");
      return false;
    }
    if (result.kind === "invalid_password") {
      window.alert("Password must be 4–64 characters.");
      return false;
    }
    if (result.kind === "blocked") {
      window.alert("That name isn't allowed. Pick another.");
      return false;
    }
    if (result.kind === "banned") {
      window.alert("This account has been banned.");
      return false;
    }
    if (result.kind === "error") {
      window.alert("Couldn't reach the leaderboard server. Try again later.");
      return false;
    }

    this.settings.setAuth(trimmed, result.token);
    await this.shop.refresh();
    this.updateSelfManaged();
    return this.settings.isLoggedIn();
  }

  private async promptForLogin(): Promise<boolean> {
    const name = sanitizeName(window.prompt("Name:", this.settings.playerName) ?? "");
    if (!isValidName(name)) return false;
    const password =
      (await promptModal({ message: `Password for "${name}":`, password: true, maxLength: 64 })) ??
      "";
    if (password.length === 0) return false;

    const result = await this.leaderboard.login(name, password);
    if (result.kind === "invalid") {
      window.alert("Wrong name or password.");
      return false;
    }
    if (result.kind === "banned") {
      window.alert("This account has been banned.");
      return false;
    }
    if (result.kind === "error") {
      window.alert("Couldn't reach the leaderboard server. Try again later.");
      return false;
    }
    this.settings.setAuth(result.name, result.token);
    await this.shop.refresh();
    this.updateSelfManaged();
    return true;
  }

  private readonly onVisibility = (): void => {
    if (document.hidden) {
      this.lastTime = performance.now();
      this.clock.reset();
      this.scenes.pauseToggle();
    }
  };

  private readonly frame = (now: number): void => {
    if (!this.running) return;

    const flags = this.mods.flags;
    let frameDt = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (flags.slowMotion) frameDt *= 0.35;

    const { steps, alpha } = this.clock.step(frameDt);
    for (let i = 0; i < steps; i += 1) {
      this.scenes.update(1 / 60);
    }

    const { ctx } = this.renderer;
    ctx.fillStyle = "#1a1f2b";
    ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);

    if (flags.upsideDownScreen) {
      ctx.save();
      ctx.translate(VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      ctx.rotate(Math.PI);
      ctx.translate(-VIRTUAL_WIDTH / 2, -VIRTUAL_HEIGHT / 2);
    }

    this.scenes.render(ctx, alpha);
    this.buttons.render(ctx);

    if (flags.upsideDownScreen) ctx.restore();

    if (flags.fakeBanScreen) drawFakeBanScreen(ctx);

    this.rafId = requestAnimationFrame(this.frame);
  };
}
